import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';

interface MiniPIGraphProps {
  factorA: number;
  factorB: number;
  factorC: number;
  factorD: number;
  idealRanges?: {
    a?: [number, number];
    b?: [number, number];
    c?: [number, number];
    d?: [number, number];
  };
  size?: 'small' | 'medium';
  showLabels?: boolean;
}

const MiniPIGraph: React.FC<MiniPIGraphProps> = ({
  factorA,
  factorB,
  factorC,
  factorD,
  idealRanges,
  size = 'small',
  showLabels = true,
}) => {
  const height = size === 'small' ? 24 : 36;
  const width = size === 'small' ? 120 : 180;
  const dotSize = size === 'small' ? 16 : 20;

  // Convert 0-100 scale to sigma scale for display
  const scaleToSigma = (value: number) => ((value - 50) / 16.67);
  
  // Round sigma to nearest 0.25
  const roundToQuarter = (sigma: number) => Math.round(sigma * 4) / 4;
  
  // Convert sigma to pixel position (0 to width)
  const sigmaToPosition = (sigma: number) => ((sigma + 3) / 6) * width;
  
  // Convert 0-100 to position
  const valueToPosition = (value: number) => sigmaToPosition(scaleToSigma(value));

  // Check if a value is within ideal range
  const isInRange = (value: number, range?: [number, number]) => {
    if (!range) return true;
    return value >= range[0] && value <= range[1];
  };

  // Get color based on whether value is in ideal range
  const getColor = (value: number, factor: 'a' | 'b' | 'c' | 'd') => {
    if (!idealRanges) return '#1976d2'; // Default blue
    const range = idealRanges[factor];
    if (isInRange(value, range)) return '#4caf50'; // Green for in range
    // Check if close (within 25% tolerance)
    if (range) {
      const tolerance = (range[1] - range[0]) * 0.25;
      if (value >= (range[0] - tolerance) && value <= (range[1] + tolerance)) {
        return '#ff9800'; // Orange for close
      }
    }
    return '#f44336'; // Red for out of range
  };

  const factors = [
    { label: 'A', value: factorA, key: 'a' as const, color: '#e53935' },
    { label: 'B', value: factorB, key: 'b' as const, color: '#1976d2' },
    { label: 'C', value: factorC, key: 'c' as const, color: '#43a047' },
    { label: 'D', value: factorD, key: 'd' as const, color: '#fb8c00' },
  ];

  return (
    <Box sx={{ width: width + (showLabels ? 20 : 0) }}>
      {factors.map((factor, index) => {
        const position = valueToPosition(factor.value);
        const color = getColor(factor.value, factor.key);
        const range = idealRanges?.[factor.key];
        const rangeStart = range ? valueToPosition(range[0]) : 0;
        const rangeEnd = range ? valueToPosition(range[1]) : width;

        return (
          <Box
            key={factor.key}
            sx={{
              position: 'relative',
              height: height,
              mb: size === 'small' ? 0.5 : 1,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {/* Factor label */}
            {showLabels && (
              <Typography
                sx={{
                  width: 20,
                  fontSize: size === 'small' ? '0.75rem' : '0.875rem',
                  color: 'text.secondary',
                  fontWeight: 'medium',
                }}
              >
                {factor.label}
              </Typography>
            )}

            {/* Scale bar container */}
            <Box
              sx={{
                position: 'relative',
                width: width,
                height: height,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {/* Background scale bar */}
              <Box
                sx={{
                  position: 'absolute',
                  width: '100%',
                  height: 4,
                  backgroundColor: 'action.hover',
                  borderRadius: 2,
                }}
              />

              {/* Ideal range highlight */}
              {idealRanges && range && (
                <Box
                  sx={{
                    position: 'absolute',
                    left: rangeStart,
                    width: rangeEnd - rangeStart,
                    height: 8,
                    backgroundColor: '#ff9800',  // Orange for ideal range
                    borderRadius: 1,
                    opacity: 0.4,
                  }}
                />
              )}

              {/* Factor dot with tooltip */}
              <Tooltip
                title={
                  <Box>
                    <Typography variant="body2">
                      Factor {factor.label}: {scaleToSigma(factor.value).toFixed(2)}σ
                    </Typography>
                    {range && (
                      <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                        Ideal: {roundToQuarter(scaleToSigma(range[0]))}σ to {roundToQuarter(scaleToSigma(range[1]))}σ
                      </Typography>
                    )}
                  </Box>
                }
              >
                <Box
                  sx={{
                    position: 'absolute',
                    left: position - dotSize / 2,
                    width: dotSize,
                    height: dotSize,
                    borderRadius: '50%',
                    backgroundColor: color,
                    border: '2px solid white',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    '&:hover': {
                      transform: 'scale(1.2)',
                      boxShadow: '0 3px 6px rgba(0,0,0,0.3)',
                    },
                  }}
                />
              </Tooltip>

              {/* Sigma scale markers (optional for larger size) */}
              {size === 'medium' && (
                <>
                  {[-3, -2, -1, 0, 1, 2, 3].map((sigma) => (
                    <Box
                      key={sigma}
                      sx={{
                        position: 'absolute',
                        left: sigmaToPosition(sigma),
                        width: 1,
                        height: 8,
                        backgroundColor: 'divider',
                        transform: 'translateX(-50%)',
                      }}
                    />
                  ))}
                </>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default MiniPIGraph;